import {
  CalendarEvent,
  CircadianPhase,
  DateTimeSet,
  TaskDataDailyTrendWithData,
  TaskItem,
  TaskItemDuration,
  TaskPriority,
  TimeFormatType,
} from '@demind-inc/core';
import dayjs from 'dayjs';

import { FirestoreTaskItem, TaskModifyOption, TaskTimeSuggestionMap } from '../data-access';
import { generateRandomHexColor, getCssVariable } from '../utils';
import { TaskLabelChartSet } from '../components';
import { ProjectFilterItem } from '../hooks';
import { unionBy } from 'lodash';

export interface TaskEventUpdateDecision {
  action: 'delete' | 'create' | 'update' | 'none';
  existingEvent?: Pick<CalendarEvent, 'eventId' | 'calendarId' | 'unSyncToCalendar'> | null;
}

export interface DetermineTaskEventActionParams {
  newTaskInfo: Partial<FirestoreTaskItem> | TaskModifyOption;
  prevTaskInfo?: Partial<FirestoreTaskItem>;
  taskId: string;
  boardId: string;
  userId: string;
  /**
   * Date-scoped event lookup.
   * This must return events whose `start.date` falls within the given day.
   */
  findEventsByDate: (date: string, userId: string) => Partial<CalendarEvent>[];
}

const colorGrayLight = getCssVariable('--color-gray-light');

/**
 * Formats the due date for updating a task.
 *
 * @param event - The event object containing the start and end dates.
 * @returns A partial `TaskItem` object with the formatted date and time.
 */
export const getFormattedDueDateForUpdateTaskFromEvent = (
  event: Pick<CalendarEvent, 'start' | 'end'>,
  autoSuggestedReason?: string
): Partial<TaskItem> => {
  if (!event.start && !event.end) {
    return {};
  }

  let task: Partial<TaskItem> = {};

  if (event.start) {
    task.dueDateTime = {
      datetime: event.start.date,
      timezone: event.start.timeZone || dayjs.tz.guess(),
      autoSuggested: !!autoSuggestedReason,
      autoSuggestedReason,
    };
  }

  if (event.start && event.end) {
    const duration = Math.abs(dayjs(event.end.date).diff(dayjs(event.start.date), 'minute'));
    task.duration = {
      amount: duration,
      unit: 'minute',
    };
  }

  return task;
};

export const translateTemporaryTasks = (
  taskSuggestions: TaskTimeSuggestionMap,
  existingTasks: TaskItem[],
  targetTaskId: string
) => {
  const temporaryTasksFromSuggestions = Object.entries(taskSuggestions)
    .filter(
      ([existingTaskId, value]) =>
        !!value.startTime &&
        !!value.endTime &&
        value.status === 'suggested' &&
        existingTaskId !== targetTaskId
    )
    .map(([existingTaskId, value]) => ({
      taskId: existingTaskId,
      name: existingTasks.find((task) => task.taskId === existingTaskId)?.name,
      startTime: dayjs(value.startTime).format('HH:mm'),
      endTime: dayjs(value.endTime).format('HH:mm'),
    }));
  const temporaryTasksFromExistingTasks = existingTasks
    .filter(
      (task) =>
        task.taskId !== targetTaskId &&
        !task.completed &&
        !task.deleted &&
        task.dueDateTime?.datetime &&
        task.duration?.amount
    )
    .map((task) => {
      const endTime = dayjs(task.dueDateTime?.datetime).format('HH:mm');
      const startTime = dayjs(task.dueDateTime?.datetime)
        .subtract(task.duration?.amount, task.duration?.unit)
        .format('HH:mm');
      return {
        taskId: task.taskId,
        name: task.name,
        startTime: startTime,
        endTime: endTime,
      };
    });

  return unionBy(temporaryTasksFromSuggestions, temporaryTasksFromExistingTasks, 'taskId');
};

export const getParamToUpdateTaskTimeFromCalendar = (
  event: Partial<CalendarEvent>,
  addedZonePhase?: CircadianPhase
): Partial<FirestoreTaskItem> => {
  return {
    ...(event.start
      ? {
          dueDateTime: {
            datetime: dayjs(event.start.date).toISOString(),
            date: '',
            timezone: dayjs.tz.guess(),
          },
        }
      : {}),
    ...(event.start && event.end
      ? {
          duration: {
            amount: dayjs(event.end.date).diff(dayjs(event.start.date), 'minute'),
            unit: 'minute',
          },
        }
      : {}),
    addedToCalendar: true,
    addedZonePhase,
  };
};

export const removeScheduledTaskTimeFromTask = (date: string): Partial<FirestoreTaskItem> => {
  return {
    startDateTime: {
      datetime: '',
      date,
      timezone: dayjs.tz.guess(),
    },
    dueDateTime: {
      datetime: '',
      date,
      timezone: dayjs.tz.guess(),
    },
    addedToCalendar: false,
  };
};

/**
 * Formats the due date for updating a task.
 *
 * @param label - Specifies whether the date is a 'start' or 'due' date.
 * @param dateSet - An optional object containing date and time information.
 * @returns A partial `TaskItem` object with the formatted date and time.
 */
export const getFormattedDueDateForUpdateTask = (
  label: 'start' | 'due',
  dateSet?: DateTimeSet
): Partial<TaskItem> => {
  const labelKey = label === 'start' ? 'startDateTime' : 'dueDateTime';
  if (!dateSet) {
    return {
      [labelKey]: undefined,
    };
  }
  const hasDataTime = !!dateSet.datetime;

  return {
    [labelKey]: {
      ...(dateSet.datetime ? { datetime: dateSet.datetime } : {}),
      ...(dateSet.date && !hasDataTime ? { date: dateSet.date } : {}), // Send date only when datetime is not set
      timezone: dateSet.timezone || dayjs.tz.guess(),
      autoSuggested: dateSet.autoSuggested,
      autoSuggestedReason: dateSet.autoSuggestedReason,
    } as DateTimeSet,
  };
};

/**
 * Formats the due date for updating a task.
 *
 * @param label - Specifies whether the date is a 'start' or 'due' date.
 * @param dateSet - An optional object containing date and time information.
 * @returns A partial `TaskItem` object with the formatted date and time.
 */
export const getFormattedDueDateForAddTask = (
  label: 'start' | 'due',
  dateSet?: DateTimeSet,
  autoSuggestion?: boolean
): Partial<TaskItem> => {
  const hasValidDate = !!dateSet && (!!dateSet.datetime || !!dateSet.date);
  if (!hasValidDate) {
    return {};
  }

  const labelKey = label === 'start' ? 'startDateTime' : 'dueDateTime';
  const hasDataTime = !!dateSet.datetime;

  return {
    [labelKey]: {
      ...(dateSet.datetime ? { datetime: dateSet.datetime } : {}),
      ...(dateSet.date && !hasDataTime ? { date: dateSet.date } : {}), // Send date only when datetime is not set
      timezone: dateSet.timezone || dayjs.tz.guess(),
      autoSuggested: autoSuggestion,
      autoSuggestedReason: dateSet.autoSuggestedReason,
    } as DateTimeSet,
  };
};

export const getStartDateTimeFromDuration = (
  duration: TaskItemDuration,
  dueDateTime: DateTimeSet
): Partial<TaskItem> => {
  if (!dueDateTime?.datetime || !duration.amount) {
    return {
      startDateTime: {
        timezone: dueDateTime?.timezone || dayjs.tz.guess(),
      },
    };
  }
  return {
    startDateTime: {
      datetime: dayjs(dueDateTime?.datetime).subtract(duration.amount, duration.unit).toISOString(),
      timezone: dueDateTime?.timezone || dayjs.tz.guess(),
    },
  };
};

export const getEndtDateTimeFromDuration = (
  duration: TaskItemDuration,
  dueDateTime: DateTimeSet
): Partial<TaskItem> => {
  if (!dueDateTime?.datetime || !duration.amount) {
    return {
      dueDateTime: {
        timezone: dueDateTime?.timezone || dayjs.tz.guess(),
      },
    };
  }
  return {
    dueDateTime: {
      datetime: dayjs(dueDateTime?.datetime).add(duration.amount, duration.unit).toISOString(),
      timezone: dueDateTime?.timezone || dayjs.tz.guess(),
    },
  };
};
/**
 * Calculates the duration between two DateTimeSet objects and returns it in minutes.
 *
 * @param startDateTime - The starting DateTimeSet object.
 * @param dueDateTime - The ending DateTimeSet object.
 * @returns A Partial<TaskItem> object containing the duration in minutes, or an empty object if either DateTimeSet is invalid.
 */
export const getFormattedDuration = (
  startDateTime?: DateTimeSet,
  dueDateTime?: DateTimeSet
): Partial<TaskItem> => {
  if (!startDateTime || !dueDateTime || !dueDateTime.datetime || !startDateTime.datetime) {
    return {};
  }

  const durationMin = Math.abs(
    dayjs(dueDateTime.datetime).diff(dayjs(startDateTime.datetime), 'minute')
  );

  return {
    duration: {
      amount: durationMin,
      unit: 'minute',
    },
  };
};

export const getFormattedOverdueTime = (task: TaskItem, timeFormat: TimeFormatType) => {
  let outputTime = '';
  const timeformat = timeFormat === '12h' ? 'h:mm A' : 'HH:mm';

  if (task.dueDateTime?.date) {
    outputTime = dayjs(task.dueDateTime.date).format('MMM D');
  } else if (task.dueDateTime?.datetime) {
    outputTime = dayjs(task.dueDateTime.datetime).format(`MMM D • ${timeformat}`);
  }

  return outputTime;
};

export const transformTaskLabelToChartSetForTrend = (
  item: TaskDataDailyTrendWithData
): TaskLabelChartSet => {
  let obj: TaskLabelChartSet = {};
  const labels = item.labels ?? [];

  labels.forEach((label) => {
    obj[label.labelName] = {
      color: label.labelColor || generateRandomHexColor(), //Assign color randomly
      plannedTaskNum: label.plannedTasks.length ?? 0,
      completedTaskNum: label.completedTasks.length ?? 0,
    };
  });

  const unlabeledPlannedTask = item.plannedTasks?.filter((t) => !t.labels?.length).length ?? 0;
  const unlabeledCompletedTask = item.completedTasks?.filter((t) => !t.labels?.length).length ?? 0;

  // Add unlabeled data
  obj.unlabeld = {
    color: colorGrayLight,
    plannedTaskNum: unlabeledPlannedTask,
    completedTaskNum: unlabeledCompletedTask,
  };

  return obj;
};

export const getDueDateYYYYMMDD = (dueDateTime?: DateTimeSet): string | undefined => {
  if (!dueDateTime) {
    return;
  }
  if (dueDateTime.date) {
    return dueDateTime.date;
  }
  if (dueDateTime.datetime) {
    return dayjs(dueDateTime.datetime).format('YYYY-MM-DD');
  }
  return;
};

export const getIsOverdue = (targetDate: string) => {
  return dayjs(targetDate).isBefore(dayjs(), 'day');
};

/**
 * Determines the appropriate default boardId based on the following priority:
 *
 * 1. If editing an existing task → return its boardId.
 * 2. If multiple projects are selected → return lastUsedBoardId if it's valid; otherwise, use the first available selected filter.
 * 3. If a single project is selected (and it's not "All") → return that project's ID.
 * 4. If "All" is selected and lastUsedBoardId is valid → return lastUsedBoardId.
 * 5. Fallback → return the first available project from the filter options (excluding "All" if possible).
 *
 * @param params - An object containing:
 *   - defaultTask: The existing task being edited, if any.
 *   - selectedFilters: List of currently selected project filters.
 *   - lastUsedBoardId: Previously used board ID.
 *   - filterProjectOptions: List of available project options.
 *
 * @returns The most appropriate boardId based on the defined priority.
 */
export const getDefaultBoardId = ({
  defaultTask,
  selectedFilters = [],
  lastUsedBoardId = '',
  filterProjectOptions = [],
}: {
  defaultTask?: TaskItem;
  selectedFilters?: ProjectFilterItem[];
  lastUsedBoardId?: string;
  filterProjectOptions?: ProjectFilterItem[];
}): string => {
  if (defaultTask?.boardId) {
    return defaultTask.boardId;
  }

  if (selectedFilters.length > 1) {
    const isLastUsedInSelected = selectedFilters.some(
      (filter) => filter.todoTaskId === lastUsedBoardId
    );
    const isLastUsedAvailable = filterProjectOptions.some(
      (option) => option.todoTaskId === lastUsedBoardId
    );

    if (isLastUsedInSelected && isLastUsedAvailable) {
      return lastUsedBoardId;
    } else {
      const firstAvailableSelected = selectedFilters.find((filter) =>
        filterProjectOptions.some((option) => option.todoTaskId === filter.todoTaskId)
      );
      return firstAvailableSelected?.todoTaskId || selectedFilters[0].todoTaskId;
    }
  }

  if (selectedFilters.length === 1 && selectedFilters[0].todoTaskId !== 'all') {
    return selectedFilters[0].todoTaskId;
  }

  if (
    lastUsedBoardId &&
    filterProjectOptions.some((option) => option.todoTaskId === lastUsedBoardId)
  ) {
    return lastUsedBoardId;
  }

  if (filterProjectOptions.length > 0) {
    const firstAvailableProject = filterProjectOptions.find(
      (option) => option.todoTaskId !== 'all'
    );
    return firstAvailableProject?.todoTaskId || filterProjectOptions[0].todoTaskId;
  }

  return '';
};

export const getDefaultBoard = ({
  selectedFilters = [],
  filterProjectOptions = [],
  lastUsedBoardId = '',
}: {
  selectedFilters?: ProjectFilterItem[];
  filterProjectOptions?: ProjectFilterItem[];
  lastUsedBoardId?: string;
}): ProjectFilterItem | undefined => {
  const lastUsedBoard = filterProjectOptions.find(
    (option) => option.todoTaskId === lastUsedBoardId
  );

  if (selectedFilters.length > 1) {
    const isLastUsedInSelected = selectedFilters.some(
      (filter) => filter.todoTaskId === lastUsedBoardId
    );

    if (isLastUsedInSelected && lastUsedBoard) {
      return lastUsedBoard;
    }
    const firstAvailableSelected = selectedFilters.find((filter) =>
      filterProjectOptions.some((option) => option.todoTaskId === filter.todoTaskId)
    );

    return firstAvailableSelected || selectedFilters[0];
  }

  if (selectedFilters.length === 1 && selectedFilters[0].todoTaskId !== 'all') {
    return selectedFilters[0];
  }

  if (selectedFilters.length === 1 && selectedFilters[0].todoTaskId == 'all' && lastUsedBoard) {
    return lastUsedBoard;
  }

  if (filterProjectOptions.length > 0) {
    const firstAvailableProject = filterProjectOptions.find(
      (option) => option.todoTaskId !== 'all'
    );
    return firstAvailableProject || filterProjectOptions[0];
  }

  return;
};

/**
 * Formats the due date for a task.
 *
 * @param dueDateTime - The due date to format.
 * @returns A string representing the formatted due date in 'MMM D' format.
 */
export const getDueDateMMMD = (dueDateTime?: DateTimeSet) => {
  if (!dueDateTime) {
    return;
  }
  if (dueDateTime.date) {
    return dayjs(dueDateTime.date).format('MMM D');
  }
  if (dueDateTime.datetime) {
    return dayjs(dueDateTime.datetime).format('MMM D');
  }
  return;
};

export const getIsDueDateWithinXDaysBefore = (
  days: number,
  dueDate?: DateTimeSet,
  targetDate?: string
) => {
  if (!targetDate || !dueDate) {
    return false;
  }
  const dueDateStr = getDueDateYYYYMMDD(dueDate);
  return (
    dayjs(dueDateStr).isAfter(dayjs(targetDate).subtract(days, 'day'), 'day') &&
    dayjs(dueDateStr).isSameOrBefore(dayjs(targetDate), 'day')
  );
};

/**
 * Sorts tasks by date category and priority.
 * Date categories: selectedDate -> overdueDate (closest first) -> futureDate -> no due date
 * Within each category: schedulability -> priority (high > medium > low > none) -> sortIndex
 *
 * @param tasks - Array of task entries [taskId, task]
 * @param targetDate - The target date to compare against (YYYY-MM-DD format)
 * @param getSchedulability - Function to determine if a task can be scheduled
 * @returns Sorted array of task entries
 */
export const sortTasksByDateAndPriority = <
  T extends { dueDateTime?: DateTimeSet; priority?: TaskPriority; sortIndex?: number },
>(
  tasks: Array<[string, T]>,
  targetDate: string,
  getSchedulability?: (task: T, targetDate: string) => boolean
): Array<[string, T]> => {
  const priorityRank: Record<TaskPriority, number> = {
    none: 3,
    high: 0,
    medium: 1,
    low: 2,
  };

  const getDateCategory = (task: T): number => {
    if (!task.dueDateTime) return 3; // No due date goes last
    const dueDate = dayjs(getDueDateYYYYMMDD(task.dueDateTime));
    const target = dayjs(targetDate);
    if (dueDate.isSame(target, 'day')) return 0; // selectedDate
    if (dueDate.isBefore(target, 'day')) return 1; // overdueDate
    return 2; // futureDate
  };

  const getDueDateValue = (task: T): number => {
    if (!task.dueDateTime) return Number.MAX_SAFE_INTEGER;
    const dueDateStr = getDueDateYYYYMMDD(task.dueDateTime);
    if (!dueDateStr) return Number.MAX_SAFE_INTEGER;
    return dayjs(dueDateStr).valueOf();
  };

  return [...tasks].sort((a, b) => {
    const taskA = a[1];
    const taskB = b[1];

    const categoryA = getDateCategory(taskA);
    const categoryB = getDateCategory(taskB);

    // First sort by date category
    if (categoryA !== categoryB) return categoryA - categoryB;

    // For overdue dates (category 1), sort by closest date first (descending)
    if (categoryA === 1 && categoryB === 1) {
      const dateA = getDueDateValue(taskA);
      const dateB = getDueDateValue(taskB);
      if (dateA !== dateB) return dateB - dateA; // More recent dates first
    }

    // For future dates (category 2), sort by earliest date first (ascending)
    if (categoryA === 2 && categoryB === 2) {
      const dateA = getDueDateValue(taskA);
      const dateB = getDueDateValue(taskB);
      if (dateA !== dateB) return dateA - dateB; // Earlier dates first
    }

    // Then by schedulability (if provided)
    if (getSchedulability) {
      const canA = getSchedulability(taskA, targetDate);
      const canB = getSchedulability(taskB, targetDate);
      if (canA && !canB) return -1;
      if (!canA && canB) return 1;
    }

    // Then by priority
    const priorityA = taskA.priority ? priorityRank[taskA.priority] : 3;
    const priorityB = taskB.priority ? priorityRank[taskB.priority] : 3;
    if (priorityA !== priorityB) return priorityA - priorityB;

    // Finally by sortIndex
    const sortIndexA = taskA.sortIndex ?? Number.MAX_SAFE_INTEGER;
    const sortIndexB = taskB.sortIndex ?? Number.MAX_SAFE_INTEGER;
    return sortIndexA - sortIndexB;
  });
};

export const transformTaskTimeToCreateCalendarEventTime = (
  task: Partial<TaskItem>
): Partial<CalendarEvent> | undefined => {
  if (task.dueDateTime?.datetime && task.duration?.amount) {
    return {
      start: {
        date: dayjs(task.dueDateTime.datetime).toISOString(),
        timeZone: dayjs.tz.guess(),
      },
      end: {
        date: dayjs(task.dueDateTime.datetime)
          .add(task.duration.amount, task.duration.unit)
          .toISOString(),
        timeZone: dayjs.tz.guess(),
      },
    };
  }
};

/**
 * Determines what action should be taken for a calendar event when a task is updated.
 * This function analyzes changes to the task (due date, name, duration) and determines
 * whether to delete, create, or update the associated calendar event.
 *
 * @param params - Parameters containing task info and event lookup function
 * @returns Decision object indicating the action to take and existing event if found
 */
export const determineTaskEventAction = (
  params: DetermineTaskEventActionParams
): TaskEventUpdateDecision => {
  const { newTaskInfo, prevTaskInfo, taskId, boardId, userId, findEventsByDate } = params;

  const prevDueDateTime = prevTaskInfo?.dueDateTime;
  const dueDateTimeExplicitlyUpdated = Object.prototype.hasOwnProperty.call(
    newTaskInfo,
    'dueDateTime'
  );
  const hasNewDueDateTime =
    newTaskInfo.dueDateTime?.datetime &&
    (!prevDueDateTime?.datetime || newTaskInfo.dueDateTime.datetime !== prevDueDateTime.datetime);
  const hasNameChanged = newTaskInfo.name && newTaskInfo.name !== prevTaskInfo?.name;
  const hasDurationChanged =
    newTaskInfo.duration?.amount &&
    (prevTaskInfo?.duration?.amount !== newTaskInfo.duration?.amount ||
      prevTaskInfo?.duration?.unit !== newTaskInfo.duration?.unit);
  const hasRequiredFieldsForEvent =
    !!newTaskInfo.dueDateTime?.datetime && !!newTaskInfo.duration?.amount;

  const prevDueDateStr = prevDueDateTime ? getDueDateYYYYMMDD(prevDueDateTime) : undefined;
  const newDueDateStr = newTaskInfo.dueDateTime
    ? getDueDateYYYYMMDD(newTaskInfo.dueDateTime as DateTimeSet)
    : undefined;

  const findAssociatedEventOnDate = (dateStr?: string) => {
    if (!dateStr) {
      return null;
    }

    const eventsOnDate = findEventsByDate(dateStr, userId) ?? [];
    const foundEvent = eventsOnDate.find(
      (event) => event.taskFrom?.taskId === taskId && event.taskFrom?.boardId === boardId
    );

    return foundEvent?.eventId
      ? {
          eventId: foundEvent.eventId,
          calendarId: foundEvent.calendarId,
          unSyncToCalendar: !!foundEvent.unSyncToCalendar,
        }
      : null;
  };

  // Try where the event is likely to exist:
  // - prev due date (normal edit)
  // - new due date (already moved / prev cache missing)
  const existingTaskEvent =
    findAssociatedEventOnDate(prevDueDateStr) ?? findAssociatedEventOnDate(newDueDateStr);
  // Only treat it as "removed" when the dueDateTime field is explicitly updated.
  // Otherwise partial updates (e.g. name-only) might accidentally look like datetime was cleared.
  const hasRemovedDueDate =
    dueDateTimeExplicitlyUpdated &&
    !!prevDueDateTime?.datetime &&
    !newTaskInfo.dueDateTime?.datetime;
  const hasClearedDueDateTimeWithExistingEvent =
    dueDateTimeExplicitlyUpdated && !newTaskInfo.dueDateTime?.datetime && !!existingTaskEvent;

  // Case 1: If due date was removed, delete the event
  if (hasRemovedDueDate || hasClearedDueDateTimeWithExistingEvent) {
    return {
      action: 'delete',
      existingEvent: existingTaskEvent,
    };
  }

  // Case 2: When due date/name/duration changed, look for existing event
  let existingEventForUpdate: Pick<
    CalendarEvent,
    'eventId' | 'calendarId' | 'unSyncToCalendar'
  > | null = null;
  if (hasNewDueDateTime || hasNameChanged || hasDurationChanged) {
    existingEventForUpdate =
      findAssociatedEventOnDate(prevDueDateStr) ?? findAssociatedEventOnDate(newDueDateStr);
  }

  const shouldCreateEvent =
    (hasNewDueDateTime || hasDurationChanged) &&
    hasRequiredFieldsForEvent &&
    !existingEventForUpdate;
  const shouldUpdateEvent =
    existingEventForUpdate &&
    (hasNewDueDateTime || hasNameChanged || hasDurationChanged) &&
    (hasRequiredFieldsForEvent || hasNameChanged || hasDurationChanged);

  if (shouldCreateEvent) {
    return {
      action: 'create',
      existingEvent: null,
    };
  }

  if (shouldUpdateEvent && existingEventForUpdate?.eventId) {
    return {
      action: 'update',
      existingEvent: existingEventForUpdate,
    };
  }

  return {
    action: 'none',
    existingEvent: null,
  };
};
